import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import {
  FileText, Plus, Send, Eye, Edit2, Copy, ChevronLeft,
  Check, X, Trash2, BarChart2, Clock, TrendingUp, AlertCircle,
  RefreshCw, Loader, Users, Download, FileCheck, FolderOpen,
} from "lucide-react";
import PrepararProyecto from "./PrepararProyecto";
import AsignarTareasModal from "./AsignarTareasModal";
import AsociarFacturaModal from "./AsociarFacturaModal";

const N8N_WEBHOOK_ENVIADA    = "https://n8n.tuaplicacion.cl/webhook/cotizacion-enviada";
const N8N_WEBHOOK_RESPONDIDA = "https://n8n.tuaplicacion.cl/webhook/cotizacion-respondida";
const APP_URL                = "https://app.tuaplicacion.cl";

const GS = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{background:#f8fafc;font-family:'DM Sans',sans-serif;color:#1e293b}
    input,textarea,select,button{font-family:'DM Sans',sans-serif}
    input:focus,textarea:focus,select:focus{outline:none;border-color:#29abe2 !important;box-shadow:0 0 0 3px #29abe215}
    ::-webkit-scrollbar{width:5px;height:5px}
    ::-webkit-scrollbar-track{background:#f1f5f9}
    ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:3px}
    @keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
    @keyframes fadeIn{from{opacity:0}to{opacity:1}}
    @keyframes spin{to{transform:rotate(360deg)}}
    .fade-up{animation:fadeUp .22s ease both}
    .spinning{animation:spin 1s linear infinite}
  `}</style>
);

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5",
  err:"#ef4444", errL:"#fee2e2",
  warn:"#f59e0b", warnL:"#fef3c7",
};

const iStyle = {
  width:"100%", padding:"8px 12px", borderRadius:7,
  border:`1px solid ${C.border}`, background:C.white,
  color:C.text, fontSize:14, outline:"none",
  transition:"border-color .15s, box-shadow .15s",
  fontFamily:"'DM Sans',sans-serif",
};

const TIPOS = ["Feature","Fix","Integración","Diseño","Consultoría","Infraestructura"];
const PAGOS = [
  "50% anticipo, 50% entrega final",
  "100% al aprobar cotización",
  "Pago en 3 cuotas mensuales iguales",
  "40% anticipo, 30% hito 1, 30% entrega",
  "Pago mensual (retainer)",
];
const ST = {
  Pendiente: {color:C.warn,  bg:C.warnL,   dot:"#f59e0b"},
  Aprobada:  {color:C.ok,    bg:C.okL,     dot:"#10b981"},
  Rechazada: {color:C.err,   bg:C.errL,    dot:"#ef4444"},
  Borrador:  {color:C.light, bg:"#f1f5f9", dot:"#94a3b8"},
  Expirada:  {color:C.light, bg:"#f1f5f9", dot:"#94a3b8"},
};

const TASK_ST = {
  "Completado":  {color:C.ok,      bg:C.okL,      dot:"#10b981"},
  "En revisión": {color:"#0284c7", bg:"#e0f2fe",  dot:"#0284c7"},
  "En progreso": {color:"#f59e0b", bg:"#fef3c7",  dot:"#f59e0b"},
  "Pendiente":   {color:C.light,   bg:"#f1f5f9",  dot:"#94a3b8"},
};

const iTotal  = (i)     => i.horas * i.ph;
const calcSub = (items) => items.reduce((s,i) => s + iTotal(i), 0);
const calcIVA = (items) => Math.round(calcSub(items) * 0.19);
const calcTot = (items) => calcSub(items) + calcIVA(items);
const calcTotal = (items, con_iva) => con_iva ? calcTot(items) : calcSub(items);
const fmtCLP  = (n)     => `$${n.toLocaleString("es-CL")}`;
const fmtDate = (d)     => d ? new Date(d+"T12:00:00").toLocaleDateString("es-CL",{day:"2-digit",month:"short",year:"numeric"}) : "";

const emptyForm = () => ({
  cliente:"", rut:"", email:"",
  fecha: new Date().toISOString().slice(0,10),
  vence: new Date(Date.now()+15*864e5).toISOString().slice(0,10),
  items: [{id:Date.now(), desc:"", tipo:"Feature", horas:8, ph:35000}],
  notas:"", condiciones:PAGOS[0], con_iva: true,
});

export default function Cotizaciones() {
  const [quotes,      setQuotes]      = useState([]);
  const [proyectosMap, setProyectosMap] = useState({});
  const [tareasEstMap, setTareasEstMap] = useState({});
  const [view,        setView]        = useState("lista");
  const [active,      setActive]      = useState(null);
  const [form,        setForm]        = useState(emptyForm());
  const [toast,       setToast]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [toDelete,    setToDelete]    = useState(null);
  const [preparando,      setPreparando]      = useState(null);
  const [asignando,       setAsignando]       = useState(null);
  const [seleccionados,   setSeleccionados]   = useState(new Set());
  const [asociandoFact,   setAsoFact]         = useState(false);
  const [ingresoDetalle,  setIngresoDetalle]  = useState(null);

  const notify = (msg, type="ok") => {
    setToast({msg, type});
    setTimeout(() => setToast(null), 3500);
  };

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    const [quotesRes, proyectosRes, tareasRes] = await Promise.all([
      supabase.from("cotizaciones").select("*").order("created_at", { ascending: false }),
      supabase.from("proyectos").select("id, cotizacion_id, cotizaciones_ids, nombre, estado"),
      supabase.from("tareas").select("cotizacion_id, estado").not("cotizacion_id", "is", null),
    ]);
    if (quotesRes.error) { notify("Error al cargar cotizaciones", "err"); console.error(quotesRes.error); }
    else setQuotes(quotesRes.data || []);
    if (!proyectosRes.error) {
      const map = {};
      (proyectosRes.data || []).forEach(p => {
        if (p.cotizacion_id) map[p.cotizacion_id] = p;
        (p.cotizaciones_ids || []).forEach(cid => { map[cid] = p; });
      });
      setProyectosMap(map);
    }
    if (!tareasRes.error) {
      const raw = {};
      (tareasRes.data || []).forEach(t => {
        if (!raw[t.cotizacion_id]) raw[t.cotizacion_id] = [];
        raw[t.cotizacion_id].push(t.estado);
      });
      const derived = {};
      Object.entries(raw).forEach(([cid, estados]) => {
        if (estados.every(e => e === "Completado"))       derived[cid] = "Completado";
        else if (estados.some(e => e === "En revisión"))  derived[cid] = "En revisión";
        else if (estados.some(e => e === "Completado"))   derived[cid] = "En progreso";
        else                                               derived[cid] = "Pendiente";
      });
      setTareasEstMap(derived);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  useEffect(() => {
    const channel = supabase
      .channel("cotizaciones_changes")
      .on("postgres_changes", { event:"*", schema:"public", table:"cotizaciones" }, () => fetchQuotes())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [fetchQuotes]);

  const saveQuote = async () => {
    if (!form.cliente || !form.email || form.items.some(i => !i.desc)) {
      notify("Completa todos los campos requeridos", "err"); return;
    }
    setSaving(true);
    const payload = {
      cliente:form.cliente, rut:form.rut, email:form.email,
      fecha:form.fecha, vence:form.vence, items:form.items,
      notas:form.notas, condiciones:form.condiciones, con_iva:form.con_iva,
    };
    if (active) {
      const { error } = await supabase.from("cotizaciones").update(payload).eq("id", active.id);
      if (error) { notify("Error al guardar", "err"); console.error(error); }
      else notify("Cotización actualizada");
    } else {
      const { error } = await supabase.from("cotizaciones").insert(payload);
      if (error) { notify("Error al crear cotización", "err"); console.error(error); }
      else notify("Cotización guardada como Borrador");
    }
    setSaving(false);
    setView("lista");
    fetchQuotes();
  };

  const sendToClient = async (q) => {
    const { error } = await supabase.from("cotizaciones").update({ estado:"Pendiente" }).eq("id", q.id);
    if (error) { notify("Error al enviar", "err"); return; }
    try {
      await fetch(N8N_WEBHOOK_ENVIADA, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          cotizacion_id:   q.id,
          cliente:         q.cliente,
          email_cliente:   q.email,
          total_clp:       calcTotal(q.items, q.con_iva !== false),
          con_iva:         q.con_iva !== false,
          vence:           q.vence,
          link_aprobacion: `${APP_URL}/?token=${q.token}`,
          condiciones:     q.condiciones,
          items_resumen:   q.items.map(i => i.desc).join(", "),
        }),
      });
    } catch (e) { console.warn("n8n webhook no disponible:", e); }
    notify(`📧 Enviada a ${q.email}`);
    fetchQuotes();
  };

  const deleteQuote = (q) => setToDelete(q);

  const confirmDelete = async () => {
    const { error } = await supabase.from("cotizaciones").delete().eq("id", toDelete.id);
    if (error) notify("Error al eliminar", "err");
    else notify("Cotización eliminada");
    setToDelete(null);
    fetchQuotes();
  };

  // ── Cuando el cliente aprueba → abrir paso intermedio
  const handleClientAction = async (q, action, comment) => {
    const { error } = await supabase.rpc("responder_cotizacion", {
      p_token:q.token, p_estado:action, p_comentario:comment,
    });
    if (error) { notify("Error al actualizar", "err"); return; }

    try {
      await fetch(N8N_WEBHOOK_RESPONDIDA, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          cotizacion_id:q.id, cliente:q.cliente, email_cliente:q.email,
          estado:action, comentario:comment, total_clp:calcTotal(q.items, q.con_iva !== false), con_iva:q.con_iva !== false,
        }),
      });
    } catch (e) { console.warn("n8n webhook no disponible:", e); }

    fetchQuotes();
    setActive(prev => prev ? {...prev, estado:action, comentario:comment} : null);

    // ← Si fue aprobada → abrir paso intermedio automáticamente
    if (action === "Aprobada") {
      const cotActualizada = { ...q, estado:"Aprobada", comentario:comment };
      setTimeout(() => {
        setView("lista");
        setPreparando(cotActualizada);
      }, 800);
    }
  };

  // ── También desde la lista: botón "Crear proyecto" para aprobadas sin proyecto
  const abrirPreparar = (q) => setPreparando(q);

  // ── Proyecto creado exitosamente
  const onProyectoCreado = (proyectoId) => {
    setPreparando(null);
    notify(`✅ Proyecto creado — ID: ${proyectoId}`);
    fetchQuotes();
  };

  const toggleSeleccionado = (id) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAsociarConfirm = () => {
    setAsoFact(false);
    setSeleccionados(new Set());
    fetchQuotes();
    notify("Cotizaciones asociadas a la factura");
  };

  const verIngresoDetalle = async (ingresoId) => {
    const { data } = await supabase
      .from("ingresos")
      .select("id, folio, fecha_emision, monto_total, estado")
      .eq("id", ingresoId)
      .maybeSingle();
    if (data) setIngresoDetalle(data);
  };

  const copyLink   = (q) => {
    navigator.clipboard?.writeText(`${APP_URL}/?token=${q.token}`);
    notify("Link copiado al portapapeles");
  };
  const portalCliente = async (q) => {
    if (!q.rut) { notify("Cliente sin portal (agregar en Contactos)", "err"); return; }
    const { data } = await supabase
      .from("contactos").select("token").eq("rut", q.rut).limit(1).maybeSingle();
    if (data?.token) {
      await navigator.clipboard.writeText(`${APP_URL}/?ctoken=${data.token}`);
      notify("Link del portal copiado");
    } else {
      notify("Cliente sin portal (agregar en Contactos)", "err");
    }
  };
  const openNew    = ()  => { setForm(emptyForm()); setActive(null); setView("form"); };
  const openEdit   = (q) => { setForm({...q, items:q.items||[]}); setActive(q); setView("form"); };
  const openClient = (q) => { setActive(q); setView("cliente"); };
  const currentQ   = active ? (quotes.find(q => q.id===active.id) || active) : null;

  return (
    <div style={{fontFamily:"'DM Sans',sans-serif", color:C.text, background:C.bg, minHeight:"100%"}}>
      <GS/>

      {/* ── Toast ── */}
      {toast && (
        <div style={{position:"fixed", top:20, right:20, zIndex:9999,
          display:"flex", alignItems:"center", gap:8,
          background:C.white, border:`1px solid ${toast.type==="err"?C.err:C.ok}40`,
          borderLeft:`3px solid ${toast.type==="err"?C.err:C.ok}`,
          borderRadius:8, padding:"11px 16px", fontSize:14,
          color:toast.type==="err"?C.err:C.ok,
          boxShadow:"0 4px 16px #00000014", animation:"fadeIn .2s ease"}}>
          {toast.type==="err" ? <AlertCircle size={15}/> : <Check size={15}/>}
          {toast.msg}
        </div>
      )}

      {/* ── Modal eliminar ── */}
      {toDelete && (
        <div style={{position:"fixed", inset:0, zIndex:9998,
          background:"rgba(15,23,42,0.4)", backdropFilter:"blur(2px)",
          display:"flex", alignItems:"center", justifyContent:"center",
          animation:"fadeIn .15s ease"}}>
          <div style={{background:C.white, borderRadius:14, padding:"28px 32px",
            width:420, boxShadow:"0 20px 60px #0000002a", border:`1px solid ${C.border}`}}>
            <div style={{width:44, height:44, borderRadius:"50%", background:C.errL,
              display:"flex", alignItems:"center", justifyContent:"center", marginBottom:16}}>
              <Trash2 size={20} color={C.err}/>
            </div>
            <div style={{fontWeight:700, fontSize:16, color:C.text, marginBottom:8}}>
              ¿Eliminar cotización?
            </div>
            <div style={{fontSize:13, color:C.sub, lineHeight:1.6, marginBottom:8}}>
              Estás a punto de eliminar la cotización
              <strong style={{color:C.text}}> {toDelete.id}</strong> de
              <strong style={{color:C.text}}> {toDelete.cliente}</strong>.
            </div>
            <div style={{fontSize:12, color:C.err, background:C.errL,
              padding:"8px 12px", borderRadius:7, marginBottom:24}}>
              ⚠ Esta acción no se puede deshacer.
            </div>
            <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
              <button onClick={() => setToDelete(null)}
                style={{padding:"8px 20px", borderRadius:8, border:`1px solid ${C.border}`,
                  background:"transparent", color:C.sub, fontSize:14, cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif", transition:"background .12s"}}
                onMouseEnter={e => e.currentTarget.style.background=C.bg}
                onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                Cancelar
              </button>
              <button onClick={confirmDelete}
                style={{padding:"8px 22px", borderRadius:8, border:"none",
                  background:C.err, color:"#fff", fontWeight:600, fontSize:14,
                  cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
                  boxShadow:`0 4px 12px ${C.err}40`, transition:"background .12s"}}
                onMouseEnter={e => e.currentTarget.style.background="#dc2626"}
                onMouseLeave={e => e.currentTarget.style.background=C.err}>
                Sí, eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal preparar proyecto ── */}
      {preparando && (
        <PrepararProyecto
          cotizacion={preparando}
          onConfirm={onProyectoCreado}
          onCancel={() => setPreparando(null)}
        />
      )}

      {/* ── Modal asignar tareas a proyecto existente ── */}
      {asignando && (
        <AsignarTareasModal
          cotizacion={asignando}
          onClose={() => setAsignando(null)}
        />
      )}

      {/* ── Modal asociar cotizaciones a factura ── */}
      {asociandoFact && (
        <AsociarFacturaModal
          cotizaciones={quotes.filter(q => seleccionados.has(q.id))}
          onClose={() => setAsoFact(false)}
          onConfirm={handleAsociarConfirm}
        />
      )}

      {/* ── Modal detalle ingreso (badge Facturada) ── */}
      {ingresoDetalle && (
        <div style={{position:"fixed", inset:0, zIndex:9995,
          background:"rgba(15,23,42,0.45)", backdropFilter:"blur(3px)",
          display:"flex", alignItems:"center", justifyContent:"center",
          padding:"24px 16px", animation:"fadeIn .15s ease"}}
          onClick={() => setIngresoDetalle(null)}>
          <div style={{background:C.white, borderRadius:14, padding:"28px 32px",
            width:400, boxShadow:"0 20px 60px #0000002a", border:`1px solid ${C.border}`,
            fontFamily:"'DM Sans',sans-serif"}}
            onClick={e => e.stopPropagation()}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20}}>
              <div style={{display:"flex", alignItems:"center", gap:10}}>
                <div style={{width:34, height:34, borderRadius:8, background:C.okL,
                  display:"flex", alignItems:"center", justifyContent:"center"}}>
                  <FileCheck size={16} color={C.ok}/>
                </div>
                <div style={{fontWeight:700, fontSize:15, color:C.text}}>Factura asociada</div>
              </div>
              <button onClick={() => setIngresoDetalle(null)}
                style={{background:"transparent", border:"none", cursor:"pointer",
                  color:C.light, padding:4, borderRadius:6, display:"flex"}}
                onMouseEnter={e => e.currentTarget.style.color = C.text}
                onMouseLeave={e => e.currentTarget.style.color = C.light}>
                <X size={17}/>
              </button>
            </div>
            {[
              ["Folio", ingresoDetalle.folio ? `Folio ${ingresoDetalle.folio}` : "—"],
              ["Fecha de emisión",    ingresoDetalle.fecha_emision ? new Date(ingresoDetalle.fecha_emision+"T12:00:00").toLocaleDateString("es-CL",{day:"2-digit",month:"short",year:"numeric"}) : "—"],
              ["Monto total",         ingresoDetalle.monto_total != null ? `$${Number(ingresoDetalle.monto_total).toLocaleString("es-CL")}` : "—"],
              ["Estado",              ingresoDetalle.estado || "—"],
            ].map(([label, val]) => (
              <div key={label} style={{display:"flex", justifyContent:"space-between",
                padding:"10px 0", borderBottom:`1px solid ${C.border}`, fontSize:14}}>
                <span style={{color:C.sub}}>{label}</span>
                <span style={{fontWeight:600, color:C.text}}>{val}</span>
              </div>
            ))}
            <button onClick={() => setIngresoDetalle(null)}
              style={{marginTop:22, width:"100%", padding:"9px", borderRadius:8,
                border:`1px solid ${C.border}`, background:"transparent", color:C.sub,
                fontSize:14, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* ════ LISTA ════ */}
      {view==="lista" && (() => {
        const cotizados  = quotes.filter(q => ["Pendiente","Aprobada"].includes(q.estado)).reduce((s,q) => s + (q.con_iva !== false ? calcTot(q.items||[]) : calcSub(q.items||[])), 0);
        const aprobadas  = quotes.filter(q => q.estado==="Aprobada").length;
        const pendientes = quotes.filter(q => q.estado==="Pendiente").length;
        const decididas  = quotes.filter(q => ["Aprobada","Rechazada"].includes(q.estado)).length;
        const tasa       = decididas>0 ? Math.round(aprobadas/decididas*100) : 0;

        const selectedQuotes = quotes.filter(q => seleccionados.has(q.id));
        const selectedRuts   = new Set(selectedQuotes.map(q => q.rut));
        const sameRut        = selectedRuts.size <= 1;

        return (
          <div style={{padding:"28px 32px"}} className="fade-up">
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24}}>
              <p style={{color:C.sub, fontSize:13}}>Gestión de propuestas y requerimientos para clientes</p>
              <div style={{display:"flex", gap:10, alignItems:"center"}}>
                {seleccionados.size > 0 && (
                  <button
                    onClick={() => { if (sameRut) setAsoFact(true); }}
                    disabled={!sameRut}
                    title={!sameRut ? "Selecciona cotizaciones del mismo cliente" : undefined}
                    style={{display:"flex", alignItems:"center", gap:6, padding:"8px 16px",
                      borderRadius:8, border:"none",
                      background: sameRut ? C.ok : "#94a3b8",
                      color:"#fff", fontWeight:600, fontSize:13,
                      cursor: sameRut ? "pointer" : "not-allowed",
                      fontFamily:"'DM Sans',sans-serif",
                      boxShadow: sameRut ? `0 4px 12px ${C.ok}40` : "none",
                      transition:"background .15s"}}
                    onMouseEnter={e => { if (sameRut) e.currentTarget.style.background="#059669"; }}
                    onMouseLeave={e => { if (sameRut) e.currentTarget.style.background=C.ok; }}>
                    <FileCheck size={14}/> Asociar a factura ({seleccionados.size})
                  </button>
                )}
                <button onClick={fetchQuotes}
                  style={{display:"flex", alignItems:"center", gap:5, padding:"7px 13px",
                    borderRadius:7, border:`1px solid ${C.border}`, background:"transparent",
                    color:C.sub, fontSize:13, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}
                  onMouseEnter={e => e.currentTarget.style.background=C.bg}
                  onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                  <RefreshCw size={13} className={loading?"spinning":""}/>
                  {loading ? "Cargando..." : "Actualizar"}
                </button>
                <button onClick={openNew}
                  style={{display:"flex", alignItems:"center", gap:6, padding:"8px 16px",
                    borderRadius:8, border:"none", background:C.accent, color:"#fff",
                    fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
                    boxShadow:`0 4px 12px ${C.accent}40`, transition:"background .15s"}}
                  onMouseEnter={e => e.currentTarget.style.background="#1a9fd4"}
                  onMouseLeave={e => e.currentTarget.style.background=C.accent}>
                  <Plus size={15}/> Nueva Cotización
                </button>
              </div>
            </div>

            <div style={{display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:24}}>
              {[
                {label:"Total cotizado",  val:fmtCLP(cotizados), icon:<TrendingUp size={17} color={C.accent}/>, color:C.accent},
                {label:"Aprobadas",       val:aprobadas,          icon:<Check size={17} color={C.ok}/>,         color:C.ok},
                {label:"En espera",       val:pendientes,         icon:<Clock size={17} color={C.warn}/>,       color:C.warn},
                {label:"Tasa aprobación", val:`${tasa}%`,         icon:<BarChart2 size={17} color="#818cf8"/>,  color:"#818cf8"},
              ].map((k,i) => (
                <div key={i} style={{background:C.white, border:`1px solid ${C.border}`,
                  borderRadius:10, padding:"16px 20px", boxShadow:"0 1px 3px #0000000a"}}>
                  <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10}}>
                    <span style={{fontSize:12, color:C.sub, fontWeight:500}}>{k.label}</span>
                    <div style={{background:C.bg, borderRadius:7, padding:6}}>{k.icon}</div>
                  </div>
                  <div style={{fontSize:24, fontWeight:700, color:k.color}}>{k.val}</div>
                </div>
              ))}
            </div>

            <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:10,
              overflow:"hidden", boxShadow:"0 1px 3px #0000000a"}}>
              <div style={{display:"grid", gridTemplateColumns:"28px 1fr 155px 120px 75px auto", gap:12,
                padding:"10px 20px", background:C.bg, borderBottom:`1px solid ${C.border}`,
                fontSize:11, fontWeight:600, color:C.light, letterSpacing:.4}}>
                <span/>
                <span>CLIENTE</span><span>TOTAL</span><span>ESTADO</span><span>ÍTEMS</span><span>ACCIONES</span>
              </div>

              {loading && (
                <div style={{padding:"40px", textAlign:"center", color:C.sub, fontSize:13,
                  display:"flex", alignItems:"center", justifyContent:"center", gap:8}}>
                  <Loader size={16} className="spinning"/> Cargando cotizaciones...
                </div>
              )}

              {!loading && quotes.length===0 && (
                <div style={{padding:"48px", textAlign:"center"}}>
                  <FileText size={32} color={C.light} style={{margin:"0 auto 12px"}}/>
                  <div style={{fontWeight:600, color:C.sub, marginBottom:6}}>Sin cotizaciones aún</div>
                  <div style={{fontSize:13, color:C.light, marginBottom:16}}>Crea tu primera propuesta</div>
                  <button onClick={openNew}
                    style={{padding:"8px 20px", borderRadius:8, border:"none",
                      background:C.accent, color:"#fff", fontWeight:600, fontSize:13,
                      cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>
                    + Nueva Cotización
                  </button>
                </div>
              )}

              {!loading && quotes.map((q, idx) => {
                const st       = ST[q.estado] || ST.Borrador;
                const items    = q.items || [];
                const proyecto = proyectosMap[q.id];
                const esSeleccionable = q.estado === "Aprobada" && !q.ingreso_id;
                const checked        = seleccionados.has(q.id);
                return (
                  <div key={q.id}
                    style={{display:"grid", gridTemplateColumns:"28px 1fr 155px 120px 75px auto", gap:12,
                      padding:"13px 20px", alignItems:"center",
                      borderBottom:idx<quotes.length-1 ? `1px solid ${C.border}` : "none",
                      background: checked ? "#f0fdf4" : "transparent",
                      transition:"background .12s"}}
                    onMouseEnter={e => { if (!checked) e.currentTarget.style.background=C.bg; }}
                    onMouseLeave={e => { if (!checked) e.currentTarget.style.background="transparent"; }}>
                    <div style={{display:"flex", alignItems:"center", justifyContent:"center"}}>
                      {esSeleccionable && (
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSeleccionado(q.id)}
                          onClick={e => e.stopPropagation()}
                          style={{cursor:"pointer", accentColor:C.ok, width:15, height:15}}
                        />
                      )}
                    </div>
                    <div>
                      <div style={{fontWeight:600, fontSize:14}}>{q.cliente}</div>
                      <div style={{fontSize:11, color:C.light, marginTop:2}}>{q.id} · {q.email}</div>
                    </div>
                    <div>
                      <div style={{display:"flex", alignItems:"center", gap:6}}>
                        <span style={{fontWeight:700, fontSize:14}}>{fmtCLP(q.con_iva !== false ? calcTot(items) : calcSub(items))}</span>
                        <span style={{fontSize:10, background: q.con_iva !== false ? "#e8f5fc" : "#f1f5f9",
                          color: q.con_iva !== false ? "#29abe2" : "#64748b", padding:"2px 6px",
                          borderRadius:4, fontWeight:600}}>
                          {q.con_iva !== false ? "IVA incl." : "Sin IVA"}
                        </span>
                      </div>
                      <div style={{fontSize:11, color:C.light, marginTop:2}}>Vence {fmtDate(q.vence)}</div>
                    </div>
                    <div style={{display:"flex", flexDirection:"column", gap:4, alignItems:"flex-start"}}>
                      <span style={{display:"inline-flex", alignItems:"center", gap:5,
                        padding:"3px 10px", borderRadius:20,
                        fontSize:12, fontWeight:500, color:st.color, background:st.bg}}>
                        <span style={{width:6, height:6, borderRadius:"50%", background:st.dot,
                          flexShrink:0, display:"inline-block"}}/>
                        {q.estado}
                      </span>
                      {q.ingreso_id && (
                        <span onClick={() => verIngresoDetalle(q.ingreso_id)}
                          style={{fontSize:11, background:"#d1fae5", color:"#059669",
                            padding:"3px 8px", borderRadius:5, fontWeight:600, cursor:"pointer",
                            display:"flex", alignItems:"center", gap:4}}>
                          <FileCheck size={11}/> Facturada
                        </span>
                      )}
                      {q.estado === "Aprobada" && (() => {
                        const tareaEst = tareasEstMap[q.id];
                        if (tareaEst) {
                          const cfg = TASK_ST[tareaEst] || TASK_ST["Pendiente"];
                          return (
                            <span title={proyecto?.nombre}
                              style={{fontSize:11, background:cfg.bg, color:cfg.color,
                                padding:"3px 8px", borderRadius:5, fontWeight:600,
                                display:"flex", alignItems:"center", gap:4}}>
                              <FolderOpen size={10}/> {tareaEst}
                            </span>
                          );
                        }
                        return (
                          <span style={{fontSize:11, background:"#f1f5f9", color:"#94a3b8",
                            padding:"3px 8px", borderRadius:5, fontWeight:500,
                            display:"flex", alignItems:"center", gap:4}}>
                            <FolderOpen size={10}/> Sin proyecto
                          </span>
                        );
                      })()}
                    </div>
                    <div style={{fontSize:13, color:C.sub}}>
                      {items.length} ítem{items.length!==1?"s":""}
                    </div>
                    <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                      <TBtn icon={<Eye size={13}/>}      label="Ver"    onClick={() => openClient(q)}/>
                      <TBtn icon={<Edit2 size={13}/>}   label="Editar" onClick={() => openEdit(q)}/>
                      <TBtn icon={<Download size={13}/>} label="PDF"    onClick={() => window.open(`${APP_URL}/?token=${q.token}&print=1`, '_blank')}/>
                      {q.estado==="Borrador" && (
                        <TBtn icon={<Send size={13}/>} label="Enviar" accent onClick={() => sendToClient(q)}/>
                      )}
                      {q.estado==="Pendiente" && (
                        <TBtn icon={<Copy size={13}/>} label="Link" onClick={() => copyLink(q)}/>
                      )}
                      {/* ← Botón crear proyecto para cotizaciones ya aprobadas */}
                      {q.estado==="Aprobada" && (
                        <TBtn icon={<FileText size={13}/>} label="Asignar a proyecto" accent onClick={() => setAsignando(q)}/>
                      )}
                      <TBtn icon={<Users size={13}/>} label="Portal cliente" onClick={() => portalCliente(q)}/>
                      <TBtn icon={<Trash2 size={13}/>} label="Eliminar" danger onClick={() => deleteQuote(q)}/>
                      {q.email && (
                        <button onClick={() => sendToClient(q)}
                          style={{display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:6,
                            border:"1px solid #bbf7d0", background:"#f0fdf4", color:"#166534",
                            fontSize:12, fontWeight:500, cursor:"pointer",
                            fontFamily:"'DM Sans',sans-serif", transition:"all .12s", whiteSpace:"nowrap"}}
                          onMouseEnter={e => e.currentTarget.style.background="#dcfce7"}
                          onMouseLeave={e => e.currentTarget.style.background="#f0fdf4"}>
                          <Send size={13}/>Reenviar
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ════ FORMULARIO ════ */}
      {view==="form" && (
        <div style={{padding:"28px 32px", maxWidth:940}} className="fade-up">
          <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:22}}>
            <button onClick={() => setView("lista")}
              style={{display:"flex", alignItems:"center", gap:5, padding:"6px 13px",
                borderRadius:7, border:`1px solid ${C.border}`, background:"transparent",
                color:C.sub, fontSize:13, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}
              onMouseEnter={e => e.currentTarget.style.background=C.bg}
              onMouseLeave={e => e.currentTarget.style.background="transparent"}>
              <ChevronLeft size={14}/> Volver
            </button>
            <div>
              <span style={{fontWeight:600, fontSize:14, color:C.text}}>
                {active ? `Editando ${active.id}` : "Nueva cotización"}
              </span>
              <span style={{fontSize:12, color:C.sub, marginLeft:8}}>
                {active ? "Modifica los datos" : "Completa los datos para crear la propuesta"}
              </span>
            </div>
          </div>

          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:18, marginBottom:18}}>
            <Card title="Datos del cliente">
              <ClienteAutocomplete form={form} setForm={setForm}/>
              <FInp label="RUT"                 field="rut"     ph="76.543.210-9"        form={form} setForm={setForm}/>
              <FInp label="Email *"             field="email"   type="email" ph="cliente@empresa.cl" form={form} setForm={setForm}/>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
                <FInp label="Fecha emisión" field="fecha" type="date" form={form} setForm={setForm}/>
                <FInp label="Vencimiento"   field="vence" type="date" form={form} setForm={setForm}/>
              </div>
            </Card>
            <Card title="Condiciones de la propuesta">
              <div style={{marginBottom:14}}>
                <label style={{display:"block", fontSize:12, fontWeight:600, color:C.sub, marginBottom:6}}>Forma de pago</label>
                <select value={form.condiciones}
                  onChange={e => setForm(f => ({...f, condiciones:e.target.value}))}
                  style={iStyle}>
                  {PAGOS.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={{display:"block", fontSize:12, fontWeight:600, color:C.sub, marginBottom:6}}>Notas adicionales</label>
                <textarea value={form.notas||""} rows={5}
                  placeholder="Ej: Incluye 30 días de soporte técnico..."
                  onChange={e => setForm(f => ({...f, notas:e.target.value}))}
                  style={{...iStyle, resize:"vertical"}}/>
              </div>
            </Card>
          </div>

          <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:10,
            overflow:"hidden", boxShadow:"0 1px 3px #0000000a", marginBottom:18}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"13px 20px", borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontWeight:600, fontSize:14}}>Requerimientos</span>
              <button onClick={() => setForm(f => ({...f, items:[...f.items,{id:Date.now(),desc:"",tipo:"Feature",horas:8,ph:35000}]}))}
                style={{display:"flex", alignItems:"center", gap:5, padding:"5px 12px", borderRadius:7,
                  border:`1px solid ${C.accent}`, background:C.accentL, color:C.accent,
                  fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>
                <Plus size={13}/> Agregar ítem
              </button>
            </div>
            <div style={{display:"grid", gridTemplateColumns:"1fr 118px 68px 108px 100px 30px", gap:8,
              padding:"8px 20px", background:C.bg, borderBottom:`1px solid ${C.border}`,
              fontSize:11, fontWeight:600, color:C.light, letterSpacing:.4}}>
              <span>DESCRIPCIÓN *</span><span>TIPO</span><span>HORAS</span><span>$/HORA</span>
              <span style={{textAlign:"right"}}>TOTAL</span><span/>
            </div>
            {form.items.map((item, idx) => (
              <div key={item.id}
                style={{display:"grid", gridTemplateColumns:"1fr 118px 68px 108px 100px 30px",
                  gap:8, padding:"9px 20px",
                  borderBottom:idx<form.items.length-1 ? `1px solid ${C.border}` : "none",
                  alignItems:"center"}}>
                <input value={item.desc} placeholder="Descripción del requerimiento..."
                  onChange={e => setForm(f => ({...f, items:f.items.map(i => i.id===item.id?{...i,desc:e.target.value}:i)}))}
                  style={{...iStyle, fontSize:13}}/>
                <select value={item.tipo}
                  onChange={e => setForm(f => ({...f, items:f.items.map(i => i.id===item.id?{...i,tipo:e.target.value}:i)}))}
                  style={{...iStyle, fontSize:12}}>
                  {TIPOS.map(t => <option key={t}>{t}</option>)}
                </select>
                <input type="number" value={item.horas}
                  onChange={e => setForm(f => ({...f, items:f.items.map(i => i.id===item.id?{...i,horas:parseInt(e.target.value)||0}:i)}))}
                  style={{...iStyle, fontSize:13, textAlign:"right"}}/>
                <input type="number" value={item.ph}
                  onChange={e => setForm(f => ({...f, items:f.items.map(i => i.id===item.id?{...i,ph:parseInt(e.target.value)||0}:i)}))}
                  style={{...iStyle, fontSize:13, textAlign:"right"}}/>
                <div style={{fontWeight:700, fontSize:13, textAlign:"right"}}>{fmtCLP(iTotal(item))}</div>
                <button onClick={() => setForm(f => ({...f, items:f.items.filter(i => i.id!==item.id)}))}
                  disabled={form.items.length===1}
                  style={{background:"transparent", border:"none", color:C.light,
                    cursor:form.items.length===1?"not-allowed":"pointer",
                    opacity:form.items.length===1?.3:1, padding:4, borderRadius:5,
                    display:"flex", alignItems:"center"}}>
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
            <div style={{padding:"14px 20px", borderTop:`1px solid ${C.border}`,
              background:C.bg, display:"flex", justifyContent:"flex-end"}}>
              <div style={{width:265}}>
                <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:8}}>
                  <button onClick={() => setForm(f=>({...f, con_iva:true}))}
                    style={{padding:"5px 14px", borderRadius:6, border:"none", cursor:"pointer",
                      background: form.con_iva ? "#29abe2" : "#e2e8f0",
                      color: form.con_iva ? "#fff" : "#64748b", fontSize:13, fontWeight:600}}>
                    Con IVA
                  </button>
                  <button onClick={() => setForm(f=>({...f, con_iva:false}))}
                    style={{padding:"5px 14px", borderRadius:6, border:"none", cursor:"pointer",
                      background: !form.con_iva ? "#0d1117" : "#e2e8f0",
                      color: !form.con_iva ? "#fff" : "#64748b", fontSize:13, fontWeight:600}}>
                    Sin IVA
                  </button>
                </div>
                {form.con_iva && [["Subtotal neto",fmtCLP(calcSub(form.items))],["IVA 19%",fmtCLP(calcIVA(form.items))]].map(([l,v]) => (
                  <div key={l} style={{display:"flex", justifyContent:"space-between",
                    padding:"3px 0", fontSize:13, color:C.sub}}>
                    <span>{l}</span><span style={{fontWeight:500}}>{v}</span>
                  </div>
                ))}
                <div style={{display:"flex", justifyContent:"space-between", padding:"10px 0 0",
                  borderTop:`2px solid ${C.border}`, marginTop:8}}>
                  <span style={{fontWeight:700, fontSize:15}}>Total CLP</span>
                  <span style={{fontWeight:800, fontSize:20, color:C.accent}}>{fmtCLP(calcTotal(form.items, form.con_iva))}</span>
                </div>
              </div>
            </div>
          </div>

          <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
            <button onClick={() => setView("lista")}
              style={{padding:"8px 20px", borderRadius:8, border:`1px solid ${C.border}`,
                background:"transparent", color:C.sub, fontSize:14, cursor:"pointer",
                fontFamily:"'DM Sans',sans-serif"}}>
              Cancelar
            </button>
            <button onClick={saveQuote} disabled={saving}
              style={{display:"flex", alignItems:"center", gap:7, padding:"8px 24px",
                borderRadius:8, border:"none", background:saving?"#94a3b8":C.accent,
                color:"#fff", fontWeight:600, fontSize:14,
                cursor:saving?"not-allowed":"pointer", fontFamily:"'DM Sans',sans-serif",
                boxShadow:saving?"none":`0 4px 12px ${C.accent}40`, transition:"background .15s"}}
              onMouseEnter={e => { if(!saving) e.currentTarget.style.background="#1a9fd4"; }}
              onMouseLeave={e => { if(!saving) e.currentTarget.style.background=saving?"#94a3b8":C.accent; }}>
              {saving
                ? <><Loader size={14} className="spinning"/>Guardando...</>
                : (active ? "Guardar cambios" : "Crear cotización")}
            </button>
          </div>
        </div>
      )}

      {/* ════ VISTA CLIENTE ════ */}
      {view==="cliente" && currentQ && (
        <ClienteViewAdmin
          quote={currentQ}
          onBack={() => setView("lista")}
          onAction={handleClientAction}
          onResend={sendToClient}
        />
      )}
    </div>
  );
}

function TBtn({ icon, label, onClick, accent, danger }) {
  return (
    <button onClick={onClick}
      style={{display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:6,
        border:`1px solid ${danger?C.err:accent?C.accent:C.border}`,
        background:danger?C.errL:accent?C.accentL:"transparent",
        color:danger?C.err:accent?C.accent:C.sub,
        fontSize:12, fontWeight:500, cursor:"pointer",
        fontFamily:"'DM Sans',sans-serif", transition:"all .12s", whiteSpace:"nowrap"}}
      onMouseEnter={e => { e.currentTarget.style.background=danger?"#fecaca":accent?`${C.accent}25`:C.bg; }}
      onMouseLeave={e => { e.currentTarget.style.background=danger?C.errL:accent?C.accentL:"transparent"; }}>
      {icon}{label}
    </button>
  );
}

function Card({ title, children }) {
  return (
    <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:10,
      padding:20, boxShadow:"0 1px 3px #0000000a"}}>
      <div style={{fontWeight:600, fontSize:13, color:C.text, marginBottom:16,
        paddingBottom:12, borderBottom:`1px solid ${C.border}`}}>
        {title}
      </div>
      {children}
    </div>
  );
}

function FInp({ label, field, type="text", ph="", form, setForm }) {
  return (
    <div style={{marginBottom:13}}>
      <label style={{display:"block", fontSize:12, fontWeight:600, color:C.sub, marginBottom:5}}>
        {label}
      </label>
      <input type={type} value={form[field]||""} placeholder={ph}
        onChange={e => setForm(f => ({...f, [field]:e.target.value}))}
        style={iStyle}/>
    </div>
  );
}

function ClienteAutocomplete({ form, setForm }) {
  const [sugerencias, setSugerencias] = useState([]);
  const [showSug,     setShowSug]     = useState(false);

  async function buscar(texto) {
    if (texto.length < 2) { setSugerencias([]); return; }
    const { data } = await supabase
      .from("contactos")
      .select("razon_social, rut, email")
      .or(`razon_social.ilike.%${texto}%,rut.ilike.%${texto}%`)
      .order("razon_social")
      .limit(8);
    setSugerencias(data || []);
  }

  function seleccionar(c) {
    setForm(f => ({ ...f, cliente: c.razon_social, rut: c.rut || "", email: c.email || "" }));
    setSugerencias([]);
    setShowSug(false);
  }

  return (
    <div style={{ marginBottom: 13 }}>
      <label style={{ display:"block", fontSize:12, fontWeight:600, color:C.sub, marginBottom:5 }}>
        Empresa / Cliente *
      </label>
      <div style={{ position:"relative" }}>
        <input
          value={form.cliente}
          placeholder="Nombre o RUT del cliente..."
          onChange={e => {
            setForm(f => ({ ...f, cliente: e.target.value }));
            buscar(e.target.value);
            setShowSug(true);
          }}
          onFocus={() => { if (form.cliente.length >= 2) setShowSug(true); }}
          onBlur={() => setTimeout(() => setShowSug(false), 150)}
          style={iStyle}
        />
        {showSug && form.cliente.length >= 2 && (
          <div style={{
            position:"absolute", top:"100%", left:0, right:0, zIndex:20, marginTop:2,
            background:"#fff", border:`1.5px solid ${C.border}`, borderRadius:8,
            boxShadow:"0 8px 24px #00000015", overflow:"hidden",
          }}>
            {sugerencias.length > 0 ? sugerencias.map(c => (
              <button
                key={c.rut || c.razon_social}
                onClick={() => seleccionar(c)}
                style={{
                  width:"100%", padding:"9px 14px", border:"none",
                  background:"transparent", cursor:"pointer", textAlign:"left",
                  fontFamily:"'DM Sans',sans-serif",
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                  borderBottom:`1px solid ${C.bg}`,
                }}
                onMouseEnter={e => e.currentTarget.style.background = C.bg}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <span style={{ fontSize:13, fontWeight:600, color:C.text }}>
                  {c.razon_social}
                </span>
                <span style={{ fontSize:12, color:C.light }}>{c.rut}</span>
              </button>
            )) : (
              <div style={{ padding:"10px 14px", fontSize:12, color:C.light, fontStyle:"italic" }}>
                Sin resultados — escribe para crear nuevo
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ClienteViewAdmin({ quote, onBack, onAction, onResend }) {
  const alreadyDecided = ["Aprobada","Rechazada"].includes(quote.estado);
  const [step,    setStep]    = useState(alreadyDecided?"done":"view");
  const [pending, setPending] = useState(null);
  const [comment, setComment] = useState(quote.comentario||"");
  const [checked, setChecked] = useState(false);
  const [firmado, setFirmado] = useState("");

  const items = quote.items || [];
  const conIva = quote.con_iva !== false;
  const tot = calcTotal(items, conIva);
  const finalEstado = alreadyDecided ? quote.estado : (step==="done" ? pending : null);
  const confirm = () => { onAction(quote, pending, comment); setStep("done"); };

  return (
    <div style={{minHeight:"100%", background:"#f0f6fb", fontFamily:"'DM Sans',sans-serif", color:C.text}}>
      <div style={{background:C.white, borderBottom:`1px solid ${C.border}`, padding:"0 32px",
        height:52, display:"flex", alignItems:"center", justifyContent:"space-between",
        boxShadow:"0 1px 4px #0000000a"}}>
        <div style={{display:"flex", alignItems:"center", gap:8}}>
          <div style={{width:26, height:26, borderRadius:6, background:C.accent,
            display:"flex", alignItems:"center", justifyContent:"center"}}>
            <FileText size={13} color="#fff"/>
          </div>
          <span style={{fontWeight:800, fontSize:14, color:C.accent}}>tu</span>
          <span style={{fontWeight:800, fontSize:14, color:C.text, marginLeft:-6}}>aplicacion</span>
          <span style={{fontSize:11, color:C.sub, marginLeft:4}}>· Vista previa del cliente</span>
        </div>
        <div style={{display:"flex", alignItems:"center", gap:10}}>
          <div style={{background:C.warnL, border:`1px solid ${C.warn}40`, borderRadius:6,
            padding:"4px 10px", fontSize:11, color:C.warn, fontWeight:600}}>
            👁 Modo preview — acciones afectan la BD
          </div>
          {quote.email && (
            <button onClick={() => onResend(quote)}
              style={{display:"flex", alignItems:"center", gap:5, padding:"5px 12px", borderRadius:7,
                border:"1px solid #bbf7d0", background:"#f0fdf4", color:"#166534",
                fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
                whiteSpace:"nowrap"}}
              onMouseEnter={e => e.currentTarget.style.background="#dcfce7"}
              onMouseLeave={e => e.currentTarget.style.background="#f0fdf4"}>
              <Send size={13}/> Reenviar
            </button>
          )}
          <button onClick={onBack}
            style={{display:"flex", alignItems:"center", gap:5, padding:"5px 12px", borderRadius:7,
              border:`1px solid ${C.border}`, background:"transparent", color:C.sub,
              fontSize:12, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}
            onMouseEnter={e => e.currentTarget.style.background=C.bg}
            onMouseLeave={e => e.currentTarget.style.background="transparent"}>
            <ChevronLeft size={13}/> Volver
          </button>
        </div>
      </div>

      <div style={{maxWidth:680, margin:"0 auto", padding:"28px 20px"}}>
        {finalEstado && (
          <div style={{display:"flex", alignItems:"center", gap:8, padding:"12px 16px",
            borderRadius:9, marginBottom:18, fontWeight:600, fontSize:14,
            background:finalEstado==="Aprobada"?C.okL:C.errL,
            border:`1px solid ${finalEstado==="Aprobada"?C.ok:C.err}40`,
            color:finalEstado==="Aprobada"?C.ok:C.err}}>
            {finalEstado==="Aprobada" ? <Check size={15}/> : <X size={15}/>}
            {finalEstado==="Aprobada"
              ? "Propuesta aprobada — se abrirá el paso para preparar el proyecto"
              : "Propuesta rechazada"}
            {quote.comentario && (
              <span style={{fontSize:12, fontWeight:400, marginLeft:4, opacity:.7}}>
                — "{quote.comentario}"
              </span>
            )}
          </div>
        )}

        <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:10,
          padding:"20px 22px", marginBottom:14, boxShadow:"0 1px 3px #0000000a"}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start"}}>
            <div>
              <div style={{fontSize:10, fontWeight:600, color:C.light, letterSpacing:1,
                textTransform:"uppercase", marginBottom:6}}>Propuesta de Desarrollo</div>
              <div style={{fontWeight:800, fontSize:22}}>{quote.id}</div>
              <div style={{fontSize:13, color:C.sub, marginTop:4}}>
                Para <strong style={{color:C.text}}>{quote.cliente}</strong>
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontWeight:800, fontSize:26, color:C.accent}}>{fmtCLP(tot)}</div>
              <div style={{fontSize:11, color:C.sub, marginTop:3}}>{conIva ? "IVA incluido" : "Sin IVA"}</div>
            </div>
          </div>
        </div>

        {!alreadyDecided && step==="view" && (
          <div style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:10,
            padding:"20px 22px", boxShadow:"0 1px 3px #0000000a"}}>
            <div style={{fontWeight:700, fontSize:14, marginBottom:14}}>¿Qué deseas hacer?</div>
            <div style={{marginBottom:12}}>
              <label style={{display:"block", fontSize:12, fontWeight:600, color:C.sub, marginBottom:5}}>
                Tu nombre completo (firma simple)
              </label>
              <input value={firmado} onChange={e => setFirmado(e.target.value)}
                placeholder="Ej: Juan Pérez González" style={iStyle}/>
            </div>
            <label style={{display:"flex", alignItems:"flex-start", gap:8, cursor:"pointer",
              fontSize:13, color:C.sub, lineHeight:1.6, marginBottom:16}}>
              <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
                style={{marginTop:3, accentColor:C.accent}}/>
              He revisado esta propuesta y acepto las condiciones indicadas.
            </label>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
              <button
                onClick={() => { if(checked&&firmado.length>2){ setPending("Aprobada"); setStep("confirm"); }}}
                style={{padding:"13px", borderRadius:9, fontWeight:600, fontSize:14,
                  cursor:checked&&firmado.length>2?"pointer":"not-allowed",
                  border:`2px solid ${checked&&firmado.length>2?C.ok:C.border}`,
                  background:checked&&firmado.length>2?C.okL:"#f8fafc",
                  color:checked&&firmado.length>2?C.ok:C.light,
                  opacity:checked&&firmado.length>2?1:.5, transition:"all .15s",
                  fontFamily:"'DM Sans',sans-serif",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:7}}>
                <Check size={15}/> Aprobar
              </button>
              <button onClick={() => { setPending("Rechazada"); setStep("confirm"); }}
                style={{padding:"13px", borderRadius:9, fontWeight:600, fontSize:14,
                  border:`2px solid ${C.border}`, background:"transparent", color:C.sub,
                  cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:7}}>
                <X size={15}/> Rechazar
              </button>
            </div>
          </div>
        )}

        {step==="confirm" && (
          <div style={{background:C.white, borderRadius:10, padding:"20px 22px",
            border:`1px solid ${pending==="Aprobada"?C.ok:C.err}40`,
            boxShadow:"0 1px 3px #0000000a"}}>
            <div style={{fontWeight:700, fontSize:14, marginBottom:6}}>
              {pending==="Aprobada" ? "Confirmar aprobación" : "Confirmar rechazo"}
            </div>
            {pending==="Rechazada" && (
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3}
                placeholder="Motivo del rechazo (opcional)..."
                style={{...iStyle, resize:"vertical", marginBottom:12}}/>
            )}
            <div style={{display:"flex", gap:10}}>
              <button onClick={() => setStep("view")}
                style={{padding:"7px 16px", borderRadius:7, border:`1px solid ${C.border}`,
                  background:"transparent", color:C.sub, fontSize:13, cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif"}}>
                Cancelar
              </button>
              <button onClick={confirm}
                style={{padding:"7px 20px", borderRadius:7, border:"none",
                  background:pending==="Aprobada"?C.ok:C.err, color:"#fff",
                  fontWeight:600, fontSize:13, cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif",
                  boxShadow:`0 4px 12px ${pending==="Aprobada"?C.ok:C.err}40`}}>
                {pending==="Aprobada" ? "Sí, aprobar" : "Sí, rechazar"}
              </button>
            </div>
          </div>
        )}

        {step==="done" && !alreadyDecided && (
          <div style={{background:pending==="Aprobada"?C.okL:C.errL, borderRadius:10,
            padding:"24px", textAlign:"center",
            border:`1px solid ${pending==="Aprobada"?C.ok:C.err}40`}}>
            <div style={{width:44, height:44, borderRadius:"50%",
              background:pending==="Aprobada"?C.ok:C.err,
              display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px"}}>
              {pending==="Aprobada" ? <Check size={20} color="#fff"/> : <X size={20} color="#fff"/>}
            </div>
            <div style={{fontWeight:700, fontSize:16,
              color:pending==="Aprobada"?C.ok:C.err, marginBottom:7}}>
              {pending==="Aprobada" ? "¡Aprobada! Preparando el proyecto..." : "Propuesta rechazada"}
            </div>
          </div>
        )}

        <div style={{textAlign:"center", marginTop:28, paddingTop:16, borderTop:`1px solid ${C.border}`}}>
          <span style={{fontWeight:800, fontSize:13, color:C.accent}}>tu</span>
          <span style={{fontWeight:800, fontSize:13, color:C.sub}}>aplicacion</span>
          <span style={{fontSize:12, color:C.light}}> · contacto@tuaplicacion.cl</span>
        </div>
      </div>
    </div>
  );
}
